import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AdminPlan } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { daysLabel, formatPrice } from '../billing/format';
import { useMe } from '../session/SessionProvider';

/** "199" o "199.50" → centavos. */
function toCents(value: FormDataEntryValue | null): number | null {
  const n = Number(String(value ?? '').replace(',', '.').trim());
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

function planFields(form: FormData) {
  const monthlyPriceCents = toCents(form.get('price'));
  const trialDays = Number(form.get('trialDays'));
  const description = String(form.get('description') ?? '').trim();
  return {
    name: String(form.get('name') ?? '').trim(),
    description: description || null,
    monthlyPriceCents,
    trialDays: Number.isInteger(trialDays) ? trialDays : 15,
    isPublic: form.get('isPublic') === 'on',
    memberLimit: String(form.get('memberLimit') ?? '').trim() ? Number(form.get('memberLimit')) : null,
    quarterlyPriceCents: String(form.get('quarterly') ?? '').trim() ? toCents(form.get('quarterly')) : null,
    semiannualPriceCents: String(form.get('semiannual') ?? '').trim() ? toCents(form.get('semiannual')) : null,
    annualPriceCents: String(form.get('annual') ?? '').trim() ? toCents(form.get('annual')) : null,
  };
}

/** Campos compartidos por "Nuevo plan" y "Editar". */
function PlanFormFields({ plan, idPrefix }: { plan?: AdminPlan; idPrefix: string }) {
  return (
    <>
      <Field label="Nombre" name="name" required minLength={2} maxLength={80} defaultValue={plan?.name} />
      <div className="form__row">
        <Field
          label={`Precio al mes${plan ? ` (${plan.currency})` : ''}`}
          name="price"
          inputMode="decimal"
          required
          defaultValue={plan ? String(plan.monthlyPriceCents / 100) : ''}
          hint="Ejemplo: 199 o 199.50"
        />
        <Field label="Días de prueba gratis" name="trialDays" type="number" min={0} max={90} required defaultValue={String(plan?.trialDays ?? 15)} />
      </div>
      <Field
        label="Miembros incluidos (capacidad)"
        name="memberLimit"
        type="number"
        min={1}
        defaultValue={plan?.memberLimit ?? ''}
        hint="Déjalo vacío para miembros ilimitados. Hoy se cuentan los miembros activos."
      />
      <div className="form__row form__row--3">
        <Field label="Precio trimestral (opcional)" name="quarterly" inputMode="decimal" defaultValue={plan?.quarterlyPriceCents != null ? String(plan.quarterlyPriceCents / 100) : ''} />
        <Field label="Precio semestral (opcional)" name="semiannual" inputMode="decimal" defaultValue={plan?.semiannualPriceCents != null ? String(plan.semiannualPriceCents / 100) : ''} />
        <Field label="Precio anual (opcional)" name="annual" inputMode="decimal" defaultValue={plan?.annualPriceCents != null ? String(plan.annualPriceCents / 100) : ''} />
      </div>
      <p className="small muted">Si dejas vacía una frecuencia, ese plan no la ofrece. El mensual siempre existe.</p>
      <div className="field">
        <label htmlFor={`${idPrefix}-description`}>Descripción (opcional)</label>
        <textarea id={`${idPrefix}-description`} name="description" maxLength={500} rows={2} defaultValue={plan?.description ?? ''} />
      </div>
      <label className="choice">
        <input type="checkbox" name="isPublic" defaultChecked={plan?.isPublic ?? true} />
        <span>Mostrarlo en la portada y al registrar una iglesia</span>
      </label>
    </>
  );
}

function PlanCard({ plan, onChanged }: { plan: AdminPlan; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const fields = planFields(new FormData(event.currentTarget));
    if (fields.monthlyPriceCents === null) {
      setError('Escribe un precio válido.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/saas/plans/${plan.code}`, fields);
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchived() {
    if (!plan.archived && !window.confirm(`¿Archivar "${plan.name}"? Ya no se ofrecerá a iglesias nuevas; las que lo tienen lo conservan.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/saas/plans/${plan.code}/${plan.archived ? 'restore' : 'archive'}`);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const badge = plan.archived ? (
    <span className="badge">Archivado</span>
  ) : plan.isPublic ? (
    <span className="badge badge--active">Visible</span>
  ) : (
    <span className="badge badge--warn">Oculto</span>
  );

  return (
    <Card title={plan.name} action={badge}>
      {error ? <Alert>{error}</Alert> : null}
      {editing ? (
        <form className="form" onSubmit={save}>
          <PlanFormFields plan={plan} idPrefix={`plan-${plan.code}`} />
          {plan.churches > 0 ? (
            <Alert tone="info">
              {plan.churches === 1 ? '1 iglesia usa' : `${plan.churches} iglesias usan`} este plan: un precio nuevo se aplica en sus siguientes
              cobros.
            </Alert>
          ) : null}
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p>
            <strong>{formatPrice(plan.monthlyPriceCents, plan.currency)}</strong> <span className="muted">al mes</span> ·{' '}
            {daysLabel(plan.trialDays)} de prueba · {plan.memberLimit ? `hasta ${plan.memberLimit} miembros` : 'miembros ilimitados'}
            {plan.quarterlyPriceCents != null ? ` · trimestral ${formatPrice(plan.quarterlyPriceCents, plan.currency)}` : ''}
            {plan.semiannualPriceCents != null ? ` · semestral ${formatPrice(plan.semiannualPriceCents, plan.currency)}` : ''}
            {plan.annualPriceCents != null ? ` · anual ${formatPrice(plan.annualPriceCents, plan.currency)}` : ''}
          </p>
          {plan.description ? <p className="muted">{plan.description}</p> : null}
          <p className="small muted">
            Código: {plan.code} · {plan.churches === 1 ? '1 iglesia' : `${plan.churches} iglesias`}
          </p>
          <div className="actions">
            {!plan.archived ? (
              <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
                Editar
              </Button>
            ) : null}
            <Button type="button" variant="ghost" busy={busy} onClick={toggleArchived}>
              {plan.archived ? 'Restaurar' : 'Archivar'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

function NewPlanForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const fields = planFields(form);
    if (fields.monthlyPriceCents === null) {
      setError('Escribe un precio válido.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/saas/plans', {
        ...fields,
        code: String(form.get('code') ?? '').trim().toLowerCase(),
        currency: String(form.get('currency') ?? 'MXN').trim().toUpperCase(),
        capabilities: ['core'],
      });
      setOpen(false);
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div>
        <Button type="button" onClick={() => setOpen(true)}>
          Nuevo plan
        </Button>
      </div>
    );
  }

  return (
    <Card title="Nuevo plan">
      <form className="form" onSubmit={create}>
        {error ? <Alert>{error}</Alert> : null}
        <PlanFormFields idPrefix="new-plan" />
        <div className="form__row">
          <Field
            label="Código"
            name="code"
            required
            pattern="[a-z0-9_-]{2,40}"
            hint="Minúsculas, sin espacios. Ejemplo: esencial. No se puede cambiar después."
          />
          <Field label="Moneda" name="currency" required defaultValue="MXN" maxLength={3} hint="No se puede cambiar después." />
        </div>
        <div className="actions">
          <Button type="submit" busy={busy}>
            Crear plan
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Administración de planes (solo Superadmin). */
export function PlansAdminPage() {
  const me = useMe();
  const plans = useAsync((signal) => api.get<{ items: AdminPlan[] }>('/saas/admin/plans', signal).then((r) => r.items), []);
  if (!me.isPlatformAdmin) return <Navigate to="/app" replace />;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Planes</h1>
        <p className="muted">Lo que pagan las iglesias por usar KEORA. Solo tú, como administración de KEORA, ves esta página.</p>
      </header>
      <NewPlanForm onCreated={plans.reload} />
      {plans.status === 'loading' ? <Loading /> : null}
      {plans.status === 'error' ? <ErrorState error={plans.error} onRetry={plans.reload} /> : null}
      {plans.status === 'ready' && plans.data.length === 0 ? <EmptyState title="Todavía no hay planes." /> : null}
      {plans.status === 'ready' ? plans.data.map((plan) => <PlanCard key={plan.code} plan={plan} onChanged={plans.reload} />) : null}
    </div>
  );
}
